/**
 * AdminRaffle.jsx — Admin widget: raffle status + void ticket management.
 *
 * Ports main.js#L11146 renderAdminRaffle.
 * Issue: #246
 *
 * Read path : /api/v2/widgets/admin-raffle (admin-tier)
 * Write path: POST /api/admin/raffle/void  (v1 route, admin-gated)
 *             — same pattern as StorageCleanup; no v2 action route needed
 */
import { Show, For, createSignal, createMemo, createEffect } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty } from '../lib/format.js';
import EMOJI from '../lib/emoji.js';
import toast from '../lib/toastStore.js';

function fmtRelDate(ts) {
  if (!ts) return '—';
  const n = Number(ts);
  const d = new Date(n > 0 && n < 20_000_000_000 ? n * 1000 : n);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month:  'short',
    day:    'numeric',
    hour:   'numeric',
    minute: '2-digit',
  });
}

function GuildBlock(props) {
  const g   = () => props.guild || {};
  const win = () => g().winnings || {};
  const top = createMemo(() => (g().topEntrants || []).slice(0, 10));
  // #289 — raw / unprocessed view fallback when no processed entrants exist
  // for the current week range. We still show the metric strip (it'll be
  // zeros) plus a banner + a flat list of whatever the lua dump contains so
  // the admin can act (e.g. trigger ingest, void stale rows, finalize).
  const raw           = () => props.raw || {};
  const rawEntries    = createMemo(() => (raw().entries || []).slice(0, 50));
  const showRawView   = () => props.hasProcessed === false;
  const selected      = () => props.selected?.guildKey === props.guildKey ? props.selected : null;

  function selectEntrant(entry) {
    const tag = entry.tag || entry.name;
    if (!tag) return;
    props.onSelect?.({
      guildKey: props.guildKey,
      guildLabel: props.label,
      tag,
      tickets: entry.tickets || 0,
      gold: entry.gold || 0,
      entries: entry.entries || entry.deposits?.length || 0,
      deposits: entry.deposits || [],
    });
  }

  return (
    <div class="v2-araffle__guild">
      <div class="v2-araffle__guild-hdr">{props.label}</div>

      <div class="v2-araffle__metrics">
        <div class="v2-araffle__metric">
          <span class="v2-araffle__mval">{qty(g().entrants || 0)}</span>
          <span class="v2-araffle__mlbl">Entrants</span>
        </div>
        <div class="v2-araffle__metric">
          <span class="v2-araffle__mval">{qty(g().tickets || 0)}</span>
          <span class="v2-araffle__mlbl">Tickets {EMOJI.raffle}</span>
        </div>
        <div class="v2-araffle__metric">
          <span class="v2-araffle__mval v2-araffle__gold">{gold(g().totalPot || 0)}</span>
          <span class="v2-araffle__mlbl">Pot {EMOJI.sales}</span>
        </div>
      </div>

      <Show when={showRawView()}>
        <div class="v2-araffle__banner" role="status">
          <strong>No processed raffle data for current week range.</strong>
          <div class="v2-araffle__banner-sub">
            <Show when={raw().fileExists}
              fallback={<span>Lua dump file not found in GSData.</span>}>
              <span>Raw lua dump:&nbsp;
                <strong>{qty(raw().entrantCount || 0)}</strong> tag(s),&nbsp;
                <strong>{qty(raw().rawEntryCount || 0)}</strong> entr{(raw().rawEntryCount === 1) ? 'y' : 'ies'},&nbsp;
                <strong class="v2-araffle__gold">{gold(raw().rawGold || 0)}</strong>,&nbsp;
                <strong>{qty(raw().rawTickets || 0)}</strong> ticket(s).
                <Show when={raw().fileMtime}>
                  &nbsp;Updated <em>{fmtRelDate(raw().fileMtime)}</em>.
                </Show>
              </span>
            </Show>
          </div>
        </div>

        <Show when={rawEntries().length}>
          <div class="v2-araffle__top-hdr">Raw Entries (unfiltered)</div>
          <div class="v2-araffle__top-list">
            <For each={rawEntries()}>{(e) => (
              <button class="v2-araffle__top-entry v2-araffle__top-entry--button" onClick={() => selectEntrant({
                tag: e.tag,
                tickets: e.tickets || 0,
                gold: e.gold || 0,
                entries: 1,
                deposits: [e],
              })}>
                <span class="v2-araffle__rank v2-araffle__mono">#{e.id}</span>
                <span class="v2-araffle__tag">{e.tag}</span>
                <span class="v2-araffle__gold v2-araffle__mono">{qty(e.tickets || 0)} {EMOJI.raffle}</span>
                <span class="v2-araffle__dim v2-araffle__mono">{gold(e.gold || 0)}</span>
              </button>
            )}</For>
          </div>
        </Show>
      </Show>

      <Show when={!showRawView() && (win().first || win().second || win().third)}>
        <div class="v2-araffle__prizes">
          <div class="v2-araffle__prize">
            <span class="v2-araffle__place">1st</span>
            <span class="v2-araffle__gold">{gold(win().first || 0)}</span>
          </div>
          <div class="v2-araffle__prize">
            <span class="v2-araffle__place">2nd</span>
            <span class="v2-araffle__gold">{gold(win().second || 0)}</span>
          </div>
          <div class="v2-araffle__prize">
            <span class="v2-araffle__place">3rd</span>
            <span class="v2-araffle__gold">{gold(win().third || 0)}</span>
          </div>
        </div>
      </Show>

      <Show when={!showRawView() && top().length}>
        <div class="v2-araffle__top-hdr">Top Entrants</div>
        <div class="v2-araffle__top-list">
          <For each={top()}>{(e, i) => (
            <button class={`v2-araffle__top-entry v2-araffle__top-entry--button${selected()?.tag === (e.tag || e.name) ? ' is-selected' : ''}`}
              onClick={() => selectEntrant(e)}>
              <span class="v2-araffle__rank">#{i() + 1}</span>
              <span class="v2-araffle__tag">{e.tag || e.name || '?'}</span>
              <span class="v2-araffle__gold v2-araffle__mono">{qty(e.tickets || 0)} {EMOJI.raffle}</span>
              <span class="v2-araffle__dim v2-araffle__mono">{gold(e.gold || 0)}</span>
            </button>
          )}</For>
        </div>
      </Show>

      <Show when={selected()}>
        <EntrantDetail detail={selected()} onVoidUser={props.onVoidUser} />
      </Show>
    </div>
  );
}

function EntrantDetail(props) {
  const d = () => props.detail || {};
  const deposits = () => d().deposits || [];
  return (
    <div class="v2-araffle__detail">
      <div class="v2-araffle__detail-head">
        <div>
          <div class="v2-araffle__detail-name">{d().tag}</div>
          <div class="v2-araffle__detail-sub">
            {qty(d().entries || deposits().length)} deposits · {qty(d().tickets || 0)} {EMOJI.raffle} · {gold(d().gold || 0)}
          </div>
        </div>
        <button class="v2-btn-sm" onClick={() => props.onVoidUser?.(d().tag)}>
          Add to void users
        </button>
      </div>
      <Show when={deposits().length} fallback={<div class="v2-araffle__dim">No individual deposit rows available.</div>}>
        <div class="v2-araffle__deposit-list">
          <For each={deposits()}>{entry => (
            <div class="v2-araffle__deposit-row">
              <span class="v2-araffle__rank v2-araffle__mono">#{entry.id}</span>
              <span>{fmtRelDate(entry.timestamp)}</span>
              <span class="v2-araffle__gold v2-araffle__mono">{qty(entry.tickets || 0)} {EMOJI.raffle}</span>
              <span class="v2-araffle__dim v2-araffle__mono">{gold(entry.gold || 0)}</span>
            </div>
          )}</For>
        </div>
      </Show>
    </div>
  );
}

export default function AdminRaffle() {
  const { data, loading, error, refetch } = useWidgetData('admin-raffle', () => ({}));

  const [voidCount, setVoidCount] = createSignal(0);
  const [voidUsers, setVoidUsers] = createSignal('');
  const [inited,    setInited]    = createSignal(false);
  const [saving,    setSaving]    = createSignal(false);
  const [selected,  setSelected]  = createSignal(null);

  const formatVoidItem = (v) =>
    typeof v === 'object' && v !== null
      ? String(v.userId || v.tag || '').trim()
      : String(v || '').trim();

  // Pre-fill form on first successful load
  createEffect(() => {
    if (data() && !inited()) {
      setInited(true);
      setVoidCount(data().voidedCount || 0);
      setVoidUsers((data().voidedUsers || []).map(formatVoidItem).filter(Boolean).join(', '));
    }
  });

  const voidedList = createMemo(() =>
    (data()?.voidedUsers || []).map((v) => {
      if (typeof v === 'object' && v !== null) {
        const uid = String(v.userId || v.tag || '').trim();
        const g = v.guildName ? ` (${v.guildName})` : '';
        return { label: `${uid}${g}`, raw: v, userId: uid };
      }
      const s = String(v || '').trim();
      return { label: s, raw: s, userId: s };
    }).filter((x) => x.userId)
  );

  function addVoidUser(tag) {
    const clean = String(tag || '').trim();
    if (!clean) return;
    const users = voidUsers().split(',').map(u => u.trim()).filter(Boolean);
    if (!users.some(u => u.toLowerCase() === clean.toLowerCase())) {
      setVoidUsers([...users, clean].join(', '));
      toast.info('Added to void users field', { detail: clean });
    } else {
      toast.info('Already in void users field', { detail: clean });
    }
  }

  async function saveVoid() {
    if (saving()) return;
    setSaving(true);
    try {
      const existingByUser = new Map();
      for (const item of data()?.voidedUsers || []) {
        if (typeof item === 'object' && item !== null && item.userId) {
          existingByUser.set(String(item.userId).trim().toLowerCase(), item);
        }
      }
      const users = voidUsers()
        .split(',')
        .map(u => u.trim())
        .filter(Boolean)
        .map(u => existingByUser.get(u.toLowerCase()) || u);
      const res = await fetch('/api/admin/raffle/void', {
        method:      'POST',
        credentials: 'same-origin',
        headers:     { 'content-type': 'application/json' },
        body: JSON.stringify({
          voidedCount: Number(voidCount()) || 0,
          voidedUsers: users,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.error) throw new Error(body?.error || `HTTP ${res.status}`);
      toast.success('Saved.');
      setInited(false); // re-init form from fresh data
      refetch();
    } catch (err) {
      toast.error('Save failed', { detail: err.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="v2-araffle">
      <div class="v2-widget-hdr">
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={() => refetch()}>↻</button>
      </div>

      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>

          <div class="v2-araffle__body">
            <GuildBlock
              guildKey="post"
              label={`${EMOJI.post} Redfur Trading Post`}
              guild={data()?.rtp}
              raw={data()?.rtpRaw}
              hasProcessed={data()?.rtpHasProcessed}
              selected={selected()}
              onSelect={setSelected}
              onVoidUser={addVoidUser}
            />
            <div class="v2-araffle__divider" />
            <GuildBlock
              guildKey="dealers"
              label={`${EMOJI.dealers} Redfur Dealers`}
              guild={data()?.rd}
              raw={data()?.rdRaw}
              hasProcessed={data()?.rdHasProcessed}
              selected={selected()}
              onSelect={setSelected}
              onVoidUser={addVoidUser}
            />
            <div class="v2-araffle__divider" />
            <GuildBlock
              guildKey="caravan"
              label={`${EMOJI.caravan || '🐎'} Redfur Trading Caravan`}
              guild={data()?.caravan}
              raw={data()?.caravanRaw}
              hasProcessed={data()?.caravanHasProcessed}
              selected={selected()}
              onSelect={setSelected}
              onVoidUser={addVoidUser}
            />
          </div>

          {/* Void ticket management — admin-only surface */}
          <div class="v2-araffle__void-section">
            <div class="v2-araffle__void-hdr">Voided Tickets</div>

            <div class="v2-araffle__void-stats">
              <span>Count:&nbsp;
                <strong class="v2-araffle__warn">{data()?.voidedCount || 0}</strong>
              </span>
              <span>Users:&nbsp;
                <strong>{voidedList().length}</strong>
              </span>
            </div>

            <Show when={voidedList().length}>
              <div class="v2-araffle__void-tags">
                <For each={voidedList()}>{item => (
                  <span class="v2-araffle__void-tag">{item.label}</span>
                )}</For>
              </div>
            </Show>

            <div class="v2-araffle__void-form">
              <label class="v2-araffle__lbl">Voided count</label>
              <input class="v2-araffle__inp" type="number" min="0"
                value={voidCount()}
                onInput={e => setVoidCount(e.target.value)} />

              <label class="v2-araffle__lbl">Voided users (comma-separated @tags)</label>
              <input class="v2-araffle__inp" type="text"
                placeholder="@User1, @User2"
                value={voidUsers()}
                onInput={e => setVoidUsers(e.target.value)} />

              <div class="v2-araffle__void-actions">
                <button class="v2-araffle__save"
                  disabled={saving()} onClick={saveVoid}>
                  {saving() ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>

        </Show>
      </Show>
    </div>
  );
}
